import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const BrandSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    header_logo: {
      image_url: '',
      alt_text: 'Site Logo',
      link_url: '/',
      width: 150,
      height: 50
    },
    footer_logo: {
      image_url: '',
      alt_text: 'Site Logo',
      link_url: '/',
      width: 150,
      height: 50
    },
    favicon: {
      favicon_48: '',
      favicon_192: ''
    }
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/brand-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      if (data.settings) {
        setFormData(data.settings);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to load brand settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${backendUrl}/api/admin/brand-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save settings');

      alert('Brand settings saved successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to save brand settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHeaderLogo = (field, value) => {
    setFormData(prev => ({
      ...prev,
      header_logo: {
        ...prev.header_logo,
        [field]: value
      }
    }));
  };

  const updateFooterLogo = (field, value) => {
    setFormData(prev => ({
      ...prev,
      footer_logo: {
        ...prev.footer_logo,
        [field]: value
      }
    }));
  };

  const updateFavicon = (field, value) => {
    setFormData(prev => ({
      ...prev,
      favicon: {
        ...prev.favicon,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading brand settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#111827] mb-2" data-testid="brand-settings-title">
            Brand Settings
          </h1>
          <p className="text-[#6B7280]">Manage your site logo and favicon</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Header Logo Section */}
          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#111827] mb-4">Header Logo</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Image URL *</label>
                <input
                  type="url"
                  required
                  value={formData.header_logo.image_url}
                  onChange={(e) => updateHeaderLogo('image_url', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                  data-testid="header-logo-url"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Alt Text *</label>
                <input
                  type="text"
                  required
                  value={formData.header_logo.alt_text}
                  onChange={(e) => updateHeaderLogo('alt_text', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Site Logo"
                  data-testid="header-logo-alt"
                />
                <p className="text-xs text-[#6B7280] mt-1">Alternative text for accessibility</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Link URL</label>
                <input
                  type="text"
                  value={formData.header_logo.link_url}
                  onChange={(e) => updateHeaderLogo('link_url', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="/"
                  data-testid="header-logo-link"
                />
                <p className="text-xs text-[#6B7280] mt-1">Where the logo should link to (default: homepage)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Width (px)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={formData.header_logo.width}
                    onChange={(e) => updateHeaderLogo('width', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    data-testid="header-logo-width"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Height (px)</label>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={formData.header_logo.height}
                    onChange={(e) => updateHeaderLogo('height', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    data-testid="header-logo-height"
                  />
                </div>
              </div>

              {/* Preview */}
              {formData.header_logo.image_url && (
                <div className="mt-4 p-4 bg-[#F9FAFB] rounded-lg">
                  <p className="text-sm font-semibold text-[#111827] mb-2">Preview:</p>
                  <img 
                    src={formData.header_logo.image_url} 
                    alt={formData.header_logo.alt_text}
                    style={{ width: `${formData.header_logo.width}px`, height: `${formData.header_logo.height}px` }}
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer Logo Section */}
          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#111827] mb-4">Footer Logo</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Image URL *</label>
                <input
                  type="url"
                  required
                  value={formData.footer_logo.image_url}
                  onChange={(e) => updateFooterLogo('image_url', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                  data-testid="footer-logo-url"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Alt Text *</label>
                <input
                  type="text"
                  required
                  value={formData.footer_logo.alt_text}
                  onChange={(e) => updateFooterLogo('alt_text', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Site Logo"
                  data-testid="footer-logo-alt"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Link URL</label>
                <input
                  type="text"
                  value={formData.footer_logo.link_url}
                  onChange={(e) => updateFooterLogo('link_url', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="/"
                  data-testid="footer-logo-link"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Width (px)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={formData.footer_logo.width}
                    onChange={(e) => updateFooterLogo('width', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    data-testid="footer-logo-width"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Height (px)</label>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={formData.footer_logo.height}
                    onChange={(e) => updateFooterLogo('height', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    data-testid="footer-logo-height"
                  />
                </div>
              </div>

              {/* Preview */}
              {formData.footer_logo.image_url && (
                <div className="mt-4 p-4 bg-[#F9FAFB] rounded-lg">
                  <p className="text-sm font-semibold text-[#111827] mb-2">Preview:</p>
                  <img 
                    src={formData.footer_logo.image_url} 
                    alt={formData.footer_logo.alt_text}
                    style={{ width: `${formData.footer_logo.width}px`, height: `${formData.footer_logo.height}px` }}
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Favicon Section */}
          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#111827] mb-4">Favicon</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Favicon 48x48px *</label>
                <input
                  type="url"
                  required
                  value={formData.favicon.favicon_48}
                  onChange={(e) => updateFavicon('favicon_48', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://... (48x48px PNG or ICO)"
                  data-testid="favicon-48"
                />
                <p className="text-xs text-[#6B7280] mt-1">For browser tabs and bookmarks. Recommended: 48x48px PNG or ICO format</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Favicon 192x192px *</label>
                <input
                  type="url"
                  required
                  value={formData.favicon.favicon_192}
                  onChange={(e) => updateFavicon('favicon_192', e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://... (192x192px PNG)"
                  data-testid="favicon-192"
                />
                <p className="text-xs text-[#6B7280] mt-1">For modern browsers and PWA. Recommended: 192x192px PNG format</p>
              </div>

              {/* Preview */}
              {formData.favicon.favicon_48 && (
                <div className="mt-4 p-4 bg-[#F9FAFB] rounded-lg">
                  <p className="text-sm font-semibold text-[#111827] mb-2">Preview (48x48px):</p>
                  <img 
                    src={formData.favicon.favicon_48} 
                    alt="Favicon 48x48"
                    style={{ width: '48px', height: '48px' }}
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
              data-testid="submit-button"
            >
              {saving ? 'Saving...' : 'Save Brand Settings'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default BrandSettings;
